import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { API } from "../App";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { FileText } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "./ui/sonner";
import { ContractorDefectGroups } from "./defects/ContractorDefectGroups";

export default function ContractorDefectsPublic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updatingId, setUpdatingId] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line
  }, [token]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/public/contractor-defects/${token}`);
      if (res.ok) {
        setData(await res.json());
      } else {
        setError("This link is invalid or has expired.");
      }
    } catch (e) {
      console.error("Error loading contractor defects:", e);
      setError("Unable to load defects. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSetStatus = async (defectId, status) => {
    setUpdatingId(defectId);
    try {
      const res = await fetch(
        `${API}/public/contractor-defects/${token}/${defectId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status }),
        }
      );
      if (res.ok) {
        toast.success(
          status === "completed" ? "Marked as complete" : "Marked as incomplete"
        );
        await loadData();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to update status");
      }
    } catch (e) {
      console.error("Error updating status:", e);
      toast.error("Failed to update status");
    } finally {
      setUpdatingId(null);
    }
  };

  const handlePrintReport = async () => {
    if (expandedCategories.length === 0) {
      toast.error("Expand at least one category to include in the report");
      return;
    }
    setPrinting(true);
    try {
      const res = await fetch(
        `${API}/public/contractor-defects/${token}/report-pdf`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            categories: expandedCategories,
            generated_at: new Date().toLocaleString("en-AU", {
              day: "2-digit",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            }),
          }),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to generate report");
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const safe = (data?.contractor_name || "contractor")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
      const link = document.createElement("a");
      link.href = url;
      link.download = `defects-report-${safe}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (e) {
      console.error("Error generating report:", e);
      toast.error("Failed to generate report");
    } finally {
      setPrinting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">
        Loading...
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <p className="text-lg font-semibold text-slate-800 mb-2">
              Link Unavailable
            </p>
            <p className="text-slate-500">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const defects = data?.defects || [];
  const openCount = defects.filter((d) => d.status !== "completed").length;

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster />
      <div className="bg-slate-900 text-white py-6 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-2 mb-4">
            <img
              src="/logo.png?v=3"
              alt="Site Pro App"
              className="w-10 h-10 object-contain bg-white rounded-md p-1"
            />
            <span className="text-lg font-bold tracking-tight">Site Pro App</span>
          </div>
          <h1 className="text-2xl font-bold" data-testid="public-contractor-name">
            Defects for Contractor: {data?.contractor_name}
          </h1>
          <p className="text-slate-300 mt-1" data-testid="public-site-name">
            at {data?.site_name}
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6">
        <div className="flex items-center justify-between gap-3 mb-4">
          <p className="text-sm text-slate-600">
            You have <span className="font-semibold">{defects.length}</span> assigned
            defect{defects.length !== 1 ? "s" : ""} ({openCount} outstanding). Mark each
            as <span className="font-medium text-green-700">Complete</span> when finished.
          </p>
          {defects.length > 0 && (
            <Button
              onClick={handlePrintReport}
              disabled={expandedCategories.length === 0 || printing}
              className="shrink-0 bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="public-print-report-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              {printing ? "Generating..." : "Print Report to PDF"}
            </Button>
          )}
        </div>
        {defects.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-slate-500">
              No defects are currently assigned to you on this site.
            </CardContent>
          </Card>
        ) : (
          <ContractorDefectGroups
            defects={defects}
            onSetStatus={handleSetStatus}
            updatingId={updatingId}
            testid="public-defects-list"
            onExpandedChange={setExpandedCategories}
          />
        )}
      </div>
    </div>
  );
}
